import { useCallback, useEffect, useRef, useState } from "react";
import "./Alarm.css";
import { Button } from "./Button";
import { Alert, EmptyState, Loading } from "./Card";
import { Icon } from "./Icon";
import { agoText as chatAgo } from "../chat/api";
import { Chip } from "./Status";
import { useChatDock } from "../chat/dock";
import { ALARMS_CHANGED, alarmApi, alarmsChanged, goText, hrefOf, kindText, toneOf } from "../lib/alarms";
import type { Alarm, AlarmPage, Unread } from "../lib/alarms";
import { errText } from "../lib/tasks";
import { tx } from "../i18n";

/* ══ 알람의 부품 — 채택 시안 안 A(Figma 58-3) · 공통 60-2 ══
   종(내비 끝) · 종 팝오버 · 알람 표. 색·크기·여백은 전부 토큰이고(`Alarm.css`), 안 읽음은
   **셋이 함께** 말한다 — 왼쪽 띠 · ● · 굵은 본문. 색만으로 가르지 않는다. */

/** 「4분 전」처럼. 1분이 안 되면 「방금」. */
export function agoText(iso: string, now: number): string {
  return chatAgo(iso, now);
}

/** 「10-01 14:02」 — 이력은 「언제」가 본문이라 상대와 절대를 함께 적는다. */
export function clockText(iso: string): string {
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 갈래 칩 — 톤 글리프 + 글자. 모르는 갈래는 코드 그대로 그린다. */
export function AlarmKindChip({ a }: { a: Pick<Alarm, "kind" | "tone"> }) {
  const t = toneOf(a.tone);
  return (
    <Chip tone={t.chip}>
      <span aria-hidden="true">{t.glyph}</span> {kindText(a.kind)}
    </Chip>
  );
}

/** 종의 숫자 상태. `null` 은 **아직 안 읽음**, `"error"` 는 **못 읽음** — 둘 다 0 이 아니다. */
export type BellCount = Unread | null | "error";

/** 「안 읽음 3 · 그중 주의 1」 — 종 단추와 팝오버 머리가 같은 말을 쓴다. */
function unreadText(c: Unread): string {
  return c.alert > 0 ? tx("alarm.unreadAlert", { n: c.unread, alert: c.alert }) : tx("alarm.unreadN", { n: c.unread });
}

/** 종 단추. 숫자는 `GET /alarms/unread` 가 따로 준다 — 목록을 세지 않는다.
 *  빨강(주의)은 색 **과** 「!」 글자로 말한다. 못 읽었으면 「?」 와 툴팁이다. */
export function BellButton({ count, open, onClick }: { count: BellCount; open?: boolean; onClick?: () => void }) {
  let badge: React.ReactNode = null;
  let label = tx("alarm.title");
  let title = tx("alarm.bellTip");
  if (count === "error") {
    badge = <span className="b-badge bell__badge--unknown">?</span>;
    label = tx("alarm.countFailedLabel");
    title = tx("alarm.countFailedTip");
  } else if (count && count.unread > 0) {
    const alert = count.alert > 0;
    badge = <span className={`b-badge${alert ? " b-badge-danger" : ""} bell__badge`}>{alert ? `! ${count.unread}` : count.unread}</span>;
    label = `${tx("alarm.title")} · ${unreadText(count)}`;
  }
  return (
    <button type="button" className={`b-btn b-btn-ghost bell${open ? " is-on" : ""}`} aria-label={label} aria-expanded={open ?? false} aria-haspopup="dialog" title={title} onClick={onClick}>
      <Icon name="bell" />
      {badge}
    </button>
  );
}

/** 팝오버 · 표가 함께 쓰는 「안 읽음」 표식 셋 중 하나(●). 나머지 둘은 띠와 굵기다(CSS). */
function Dot({ unread }: { unread: boolean }) {
  return (
    <span className={`aldot${unread ? " aldot--on" : ""}`} aria-hidden="true">
      {unread ? "●" : "○"}
    </span>
  );
}

/** 팝오버의 한 줄 — 누르면 읽음이 남고 갈 곳으로 간다(줄마다 읽음 단추는 없다). */
export function AlarmItem({ a, now, onPick }: { a: Alarm; now: number; onPick?: (a: Alarm) => void }) {
  return (
    <li>
      <button type="button" className={`alitem${a.read ? "" : " alitem--unread"}${a.resolvedAt ? " alitem--resolved" : ""}`} onClick={() => onPick?.(a)} aria-label={`${a.read ? "" : `${tx("alarm.unread")} · `}${kindText(a.kind)} · ${a.taskTitle} — ${a.body}`}>
        <Dot unread={!a.read} />
        <span className="alitem__col">
          <span className="alitem__top">
            <AlarmKindChip a={a} />
            <span className="alitem__title">「{a.taskTitle}」</span>
          </span>
          <span className="alitem__body">{a.body}</span>
          <span className="alitem__meta">
            {a.runner ?? a.taskId ?? "—"} · {agoText(a.createdAt, now)}
            {a.resolvedAt ? ` · ${tx("alarm.resolved", { ago: agoText(a.resolvedAt, now) })}` : null} → {goText(a)}
          </span>
        </span>
      </button>
    </li>
  );
}

/** 목록 상태 셋 — 로딩 · 오류 · 빈. 팝오버와 화면이 같은 문장을 쓴다(공통 60-2 ③). */
export function AlarmListState({
  loading,
  err,
  empty,
  filtered,
  onRetry,
}: {
  loading?: boolean;
  err?: string | null;
  empty?: boolean;
  /** 거르개가 걸려 있나 — 빈 상태의 문장이 다르다. */
  filtered?: boolean;
  onRetry?: () => void;
}) {
  if (err)
    return (
      <div className="alstate">
        <Alert tone="error">{tx("alarm.loadFailed", { err })}</Alert>
        {onRetry ? (
          <Button size="sm" onClick={onRetry}>
            {tx("common.action.retry")}
          </Button>
        ) : null}
      </div>
    );
  if (loading)
    return (
      <div className="alstate">
        <Loading text={tx("alarm.loading")} />
      </div>
    );
  if (empty)
    return filtered ? (
      <EmptyState icon="check" title={tx("alarm.empty.filtered")} desc={tx("alarm.empty.filteredDesc")} />
    ) : (
      <EmptyState icon="check" title={tx("alarm.empty.none")} desc={tx("alarm.empty.noneDesc")} />
    );
  return null;
}

type PanelState = { kind: "loading" } | { kind: "error"; msg: string } | { kind: "ok"; page: AlarmPage };

/** 종 팝오버의 몸통 — 머리(수 · 모두 읽음) · 최근 30줄 · 「전체 보기」. 패턴 페이지가 이것만
 *  따로 그린다(데이터 없이). */
export function BellPanel({
  state,
  count,
  now,
  busy,
  onPick,
  onReadAll,
  onRetry,
  inline,
}: {
  /** 패턴 페이지처럼 흐름 안에 세울 때 — 떠 있는 위치를 끈다. */
  inline?: boolean;
  state: PanelState;
  count: BellCount;
  now: number;
  busy?: boolean;
  onPick?: (a: Alarm) => void;
  onReadAll?: () => void;
  onRetry?: () => void;
}) {
  const known = count && count !== "error" ? count : null;
  return (
    <div className={`b-popover bellpanel${inline ? " bellpanel--inline" : ""}`} role="dialog" aria-label={tx("alarm.title")}>
      <div className="b-popover-head bellpanel__head">
        <span>{tx("alarm.title")}</span>
        <span className="bellpanel__count">
          {known ? unreadText(known) : count === "error" ? tx("alarm.countUnknown") : "…"}
        </span>
        <span className="bellpanel__grow" />
        <Button size="sm" variant="ghost" onClick={onReadAll} disabled={!known || known.unread === 0 || busy}>
          {tx("alarm.readAll")}
        </Button>
      </div>
      {state.kind === "loading" ? <AlarmListState loading /> : null}
      {state.kind === "error" ? <AlarmListState err={state.msg} onRetry={onRetry} /> : null}
      {state.kind === "ok" ? (
        state.page.items.length ? (
          <ul className="bellpanel__list">
            {state.page.items.map((a) => (
              <AlarmItem key={a.id} a={a} now={now} onPick={onPick} />
            ))}
          </ul>
        ) : (
          <AlarmListState empty />
        )
      ) : null}
      <div className="bellpanel__foot">
        <a href="#/alarms" className="bellpanel__all">
          {tx("alarm.seeAll")}
        </a>
        {state.kind === "ok" && state.page.total > state.page.items.length ? (
          <span className="bellpanel__more">
            {tx("alarm.recentOf", { total: state.page.total, n: state.page.items.length })}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** 알람을 눌렀을 때 — 읽음을 남기고 갈 곳으로 간다. 세션 실패는 주소가 아니라 겉창이다.
 *  읽음 쓰기가 실패해도 이동은 한다(사람이 가려던 곳을 막지 않는다) — 수는 다음 읽기에 맞는다. */
export function useAlarmPick(after?: () => void) {
  const { openDock } = useChatDock();
  return useCallback(
    async (a: Alarm) => {
      if (!a.read) {
        try {
          await alarmApi.read([a.id]);
        } catch {
          // 이동이 먼저다. 못 남긴 읽음은 종의 수가 그대로인 것으로 드러난다.
        }
        alarmsChanged();
      }
      after?.();
      const href = hrefOf(a);
      if (href === null) openDock({ sessionId: a.sessionId ?? undefined, taskId: a.taskId ?? undefined });
      else if (location.hash !== href) location.hash = href;
    },
    [after, openDock],
  );
}

/** 내비 끝의 종 — 숫자는 **화면을 열 때 · 주소가 바뀔 때 · 읽음을 남긴 뒤** 읽는다. 타이머가
 *  없다(채팅 단추와 같은 규칙). 목록은 팝오버를 **열 때만** 받는다. */
export function Bell() {
  const [count, setCount] = useState<BellCount>(null);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<PanelState>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  const readCount = useCallback(() => {
    alarmApi
      .unread()
      .then(setCount)
      .catch(() => setCount("error"));
  }, []);
  const readList = useCallback(() => {
    setPanel({ kind: "loading" });
    alarmApi
      .list({ limit: 30 })
      .then((page) => setPanel({ kind: "ok", page }))
      .catch((e) => setPanel({ kind: "error", msg: errText(e) }));
  }, []);

  useEffect(() => {
    readCount();
    const onChange = () => {
      readCount();
    };
    addEventListener("hashchange", onChange);
    addEventListener(ALARMS_CHANGED, onChange);
    return () => {
      removeEventListener("hashchange", onChange);
      removeEventListener(ALARMS_CHANGED, onChange);
    };
  }, [readCount]);

  /* 열려 있는 동안만 밖 누르기 · Esc 를 듣는다. 팝오버는 서랍이 아니라 입력칸이 없어서 밖을
     누르면 닫혀도 잃는 것이 없다. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = useAlarmPick(() => setOpen(false));

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next) {
      readCount();
      readList();
    }
  }

  async function readAll() {
    setBusy(true);
    try {
      await alarmApi.readAll();
      alarmsChanged();
      readList();
    } catch (e) {
      setPanel({ kind: "error", msg: errText(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="b-menu-wrap" ref={wrap}>
      <BellButton count={count} open={open} onClick={toggle} />
      {open ? <BellPanel state={panel} count={count} now={Date.now()} busy={busy} onPick={pick} onReadAll={readAll} onRetry={readList} /> : null}
    </div>
  );
}

/** 알람 화면의 표 — 공통 `.b-table`. 줄을 누르면 읽음 + 이동이고, 칸을 고르면 바닥 막대가 뜬다 — 고른 것이
 *  없으면 막대 자체가 없다(못 누르는 단추를 띄워 두지 않는다). 좁은 화면에서도 카드로 접지 않고 틀 안에서
 *  가로로 구른다(공통 규칙 — 관제 표는 열을 맞대 보는 것이 목적이다). */
export function AlarmTable({
  rows,
  selected,
  now,
  busy,
  onToggle,
  onToggleAll,
  onPick,
  onRead,
  onClear,
}: {
  rows: Alarm[];
  selected: Set<string>;
  now: number;
  busy?: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
  onPick: (a: Alarm) => void;
  onRead: () => void;
  onClear: () => void;
}) {
  const allOn = rows.length > 0 && rows.every((a) => selected.has(a.id));
  const picked = rows.filter((a) => selected.has(a.id)).length;
  return (
    <div className="altable">
      <div className="b-table-wrap">
      <table className="b-table">
        <thead>
          <tr>
            <th className="altable__cb">
              <label className="b-check">
                <input type="checkbox" aria-label={tx("alarm.table.pickAll")} checked={allOn} disabled={!rows.length || busy} onChange={onToggleAll} />
              </label>
            </th>
            <th>{tx("alarm.table.kind")}</th>
            <th>{tx("alarm.table.body")}</th>
            <th>{tx("chat.issue")}</th>
            <th>{tx("alarm.table.when")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr
              key={a.id}
              className={`altable__row${a.read ? "" : " altable__row--unread"}${a.resolvedAt ? " altable__row--resolved" : ""}${selected.has(a.id) ? " is-selected" : ""}`}
              aria-selected={selected.has(a.id) || undefined}
              tabIndex={0}
              onClick={() => onPick(a)}
              onKeyDown={(e) => e.key === "Enter" && onPick(a)}
            >
              <td className="altable__cb" onClick={(e) => e.stopPropagation()}>
                <label className="b-check">
                  <input type="checkbox" aria-label={tx("alarm.table.pick", { title: a.taskTitle })} checked={selected.has(a.id)} disabled={busy} onChange={() => onToggle(a.id)} />
                </label>
              </td>
              <td className="altable__kind" data-label={tx("alarm.table.kind")}>
                <Dot unread={!a.read} />
                <AlarmKindChip a={a} />
              </td>
              <td className="altable__body" data-label={tx("alarm.table.body")}>
                <span className="altable__title">「{a.taskTitle}」</span> — {a.body}
                {a.resolvedAt ? <span className="altable__resolved">{tx("alarm.resolved", { ago: agoText(a.resolvedAt, now) })}</span> : null}
                <span className="altable__go">→ {goText(a)}</span>
              </td>
              <td className="altable__issue" data-label={tx("chat.issue")}>
                {a.taskId ? <code>{a.taskId}</code> : a.runner ? <code>{a.runner}</code> : <span title={tx("alarm.table.gone")}>—</span>}
              </td>
              <td className="altable__when" data-label={tx("alarm.table.when")}>
                {agoText(a.createdAt, now)} · {clockText(a.createdAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {picked ? (
        <div className="albar">
          <span className="albar__count">{tx("alarm.table.picked", { n: picked })}</span>
          <span className="albar__grow" />
          <Button size="md" variant="ghost" onClick={onClear} disabled={busy}>
            {tx("alarm.table.clear")}
          </Button>
          <Button size="md" variant="primary" onClick={onRead} loading={busy}>
            {tx("alarm.table.markRead")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
